import type { BrowserFilterConfig } from '@/components/ObjectBrowser';
import {
  ROLE_OPTIONS,
  CAPABILITY_OPTIONS,
  SPECIALISATION_OPTIONS,
  CHANNEL_EXPERTISE_OPTIONS,
} from '@sizzlenz/shared';

/** Static filter configurations (no async data needed) */
const STATIC_DIRECTORY_FILTER_CONFIGS: BrowserFilterConfig[] = [
  {
    key: 'role',
    label: 'Role',
    options: ROLE_OPTIONS.map(({ id, label }) => ({ id, label })),
    displayMode: 'combobox',
  },
  {
    key: 'capability',
    label: 'Capability',
    options: CAPABILITY_OPTIONS.map(({ id, label }) => ({ id, label })),
    displayMode: 'combobox',
  },
  {
    key: 'specialisation',
    label: 'Specialisation',
    options: SPECIALISATION_OPTIONS.map(({ id, label }) => ({ id, label })),
    displayMode: 'combobox',
  },
  {
    key: 'channelExpertise',
    label: 'Channel Expertise',
    options: CHANNEL_EXPERTISE_OPTIONS.map(({ id, label }) => ({ id, label })),
    displayMode: 'combobox',
  },
];

/**
 * Shared filter configurations for entity directory views.
 * Used by /pages/directory and HubView DirectoryTabContent.
 *
 * Kept as the original static array for backward compatibility.
 */
export const DIRECTORY_FILTER_CONFIGS: BrowserFilterConfig[] = STATIC_DIRECTORY_FILTER_CONFIGS;

/**
 * Build the full directory filter configs including dynamic country filters.
 * Pass country options from useCountries() to populate the geo filters.
 */
export function getDirectoryFilterConfigs(
  countryOptions?: { id: string; label: string }[]
): BrowserFilterConfig[] {
  const geoFilters: BrowserFilterConfig[] = countryOptions
    ? [
        {
          key: 'geoPresence',
          label: 'Country Presence',
          options: countryOptions,
          displayMode: 'combobox',
        },
        {
          key: 'geoExpertise',
          label: 'Country Expertise',
          options: countryOptions,
          displayMode: 'combobox',
        },
      ]
    : [];

  return [...STATIC_DIRECTORY_FILTER_CONFIGS, ...geoFilters];
}

/** URL sync filter definitions for directory views */
export const DIRECTORY_URL_SYNC_FILTERS = {
  search: { paramName: 'search', defaultValue: '' },
  role: { paramName: 'role', defaultValue: [] as string[], isArray: true as const },
  capability: { paramName: 'capability', defaultValue: [] as string[], isArray: true as const },
  specialisation: {
    paramName: 'specialisation',
    defaultValue: [] as string[],
    isArray: true as const,
  },
  channelExpertise: {
    paramName: 'channelExpertise',
    defaultValue: [] as string[],
    isArray: true as const,
  },
  geoPresence: {
    paramName: 'geoPresence',
    defaultValue: [] as string[],
    isArray: true as const,
  },
  geoExpertise: {
    paramName: 'geoExpertise',
    defaultValue: [] as string[],
    isArray: true as const,
  },
};

/** Build initialFilters record from URL sync values */
export function buildDirectoryInitialFilters(values: {
  role: string[];
  capability: string[];
  specialisation: string[];
  channelExpertise: string[];
  geoPresence: string[];
  geoExpertise: string[];
}): Record<string, string[]> {
  const f: Record<string, string[]> = {};
  if (values.role.length > 0) f.role = values.role;
  if (values.capability.length > 0) f.capability = values.capability;
  if (values.specialisation.length > 0) f.specialisation = values.specialisation;
  if (values.channelExpertise.length > 0) f.channelExpertise = values.channelExpertise;
  if (values.geoPresence.length > 0) f.geoPresence = values.geoPresence;
  if (values.geoExpertise.length > 0) f.geoExpertise = values.geoExpertise;
  return f;
}

/** Sync filter changes back to URL handlers */
export function syncDirectoryFilters(
  filters: Record<string, string[]>,
  handlers: {
    role: (v: string[]) => void;
    capability: (v: string[]) => void;
    specialisation: (v: string[]) => void;
    channelExpertise: (v: string[]) => void;
    geoPresence: (v: string[]) => void;
    geoExpertise: (v: string[]) => void;
  }
) {
  handlers.role(filters.role || []);
  handlers.capability(filters.capability || []);
  handlers.specialisation(filters.specialisation || []);
  handlers.channelExpertise(filters.channelExpertise || []);
  handlers.geoPresence(filters.geoPresence || []);
  handlers.geoExpertise(filters.geoExpertise || []);
}

/** Build entity API query params from browser filter state */
export function buildDirectoryApiQuery(params: {
  search?: string;
  filters: Record<string, string[]>;
  cursor?: string;
  limit: number;
  sessionHash?: string;
  tags?: string[];
  tagExpression?: Record<string, any>;
  requiredSystemTags?: string[];
  optionalSystemTags?: string[];
}) {
  const roles = params.filters.role || [];
  const capabilities = params.filters.capability || [];
  const specialisations = params.filters.specialisation || [];
  const channelExpertise = params.filters.channelExpertise || [];
  const geoPresence = params.filters.geoPresence || [];
  const geoExpertise = params.filters.geoExpertise || [];

  return {
    search: params.search,
    roles: roles.length > 0 ? roles : undefined,
    capabilities: capabilities.length > 0 ? capabilities : undefined,
    specialisations: specialisations.length > 0 ? specialisations : undefined,
    channelExpertise: channelExpertise.length > 0 ? channelExpertise : undefined,
    geoPresence: geoPresence.length > 0 ? geoPresence : undefined,
    geoExpertise: geoExpertise.length > 0 ? geoExpertise : undefined,
    tags: params.tags,
    tagExpression: params.tagExpression,
    requiredSystemTags: params.requiredSystemTags,
    optionalSystemTags: params.optionalSystemTags,
    sessionHash: params.sessionHash,
    limit: params.limit,
    nextCursor: params.cursor || undefined,
  };
}
